import { debounce } from "lodash";
import { FormControl, FormControlProps, FormHelperText, Input as StandardInput, FilledInput, OutlinedInput, InputLabel, SxProps, Theme } from "@mui/material";
import { ChangeEventHandler, ClipboardEvent, FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { Help } from "components/Help";
import styles from "./styles";

type Props = Omit<FormControlProps, "onChange"> & {
  name: string;
  type?: string;
  value?: number | string;
  label?: string;
  tooltip?: string;
  helperText?: string;
  maxLength?: number;
  multiline?: boolean;
  rows?: number;
  sx?: SxProps<Theme>;
  onChange?: (name: string, value: number | string) => void;
  onPaste?: (e: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

const InputField: FC<Props> = ({
  name,
  type,
  label,
  placeholder,
  variant = "outlined",
  tooltip,
  helperText,
  maxLength,
  multiline,
  rows,
  error,
  sx = {},
  onChange,
  onPaste,
  ...rest
}) => {
  const [value, setValue] = useState<number | string>(rest.value ?? (type === "number" ? 0 : ""));
  const Input = variant === "outlined" ? OutlinedInput : variant === "filled" ? FilledInput : StandardInput;

  useEffect(() => {
    setValue(rest.value ?? (type === "number" ? 0 : ""));
  }, [rest.value, type]);

  const commitChange = useCallback((inputValue: string) => {
    onChange?.(name, type === "number" ? (Number.parseFloat(inputValue || "0") as number) : inputValue);
  }, [name, onChange, type]);

  const debouncedChange = useMemo(() => debounce(commitChange, 250), [commitChange]);

  useEffect(() => () => debouncedChange.cancel(), [debouncedChange]);

  const handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback((e) => {
    setValue(e.target.value);
    debouncedChange(e.target.value);
  }, [debouncedChange]);

  return (
    <FormControl variant={variant} sx={sx} {...rest} className="RlxInputField">
      {label && (
        <InputLabel shrink htmlFor={`input-field-${name}`} sx={styles.label}>
          {label}
          {tooltip && <Help text={tooltip} />}
        </InputLabel>
      )}
      <Input
        id={`input-field-${name}`}
        name={name}
        type={type}
        value={value}
        placeholder={placeholder}
        multiline={multiline}
        rows={rows}
        inputProps={{ maxLength }}
        onPaste={onPaste}
        onChange={handleChange}
        sx={styles.input}
      />
      {error && <FormHelperText sx={styles.error}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

InputField.displayName = "InputField";

export default memo(InputField);
